'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { Paywall } from '@/components/Paywall';
import { cx } from '@/utils/cx';
import { AccountSettingsDialog } from '@/components/AccountSettingsDialog';
import Image from 'next/image';
import ImageIconSvg from '@/svg/icons/image.svg';

interface NavbarLinksProps {
  isLoggedIn: boolean;
  isProUser: boolean;
}

export default function NavbarLinks({
  isLoggedIn,
  isProUser,
}: {
  isLoggedIn: boolean;
  isProUser: boolean;
}) {
  const pathname = usePathname();

  return (
    <div className="nav-padd min-h-[60px] items-center px-5 xl:px-0 flex flex-row justify-between">
      <div className="flex items-center space-x-2.5 justify-self-start">
        <Link href="/" className="mr-4 hidden md:flex">
          <Image
            src={'/beatwave-logo.png'}
            alt={'Beatwave logo'}
            width={2040}
            height={458}
            className={'h-auto w-40'}
          />
        </Link>

        {isLoggedIn !== null && (
          <>
            <Link
              className={`${
                pathname?.includes('/create')
                  ? 'bg-primary/20 text-primary'
                  : 'text-white hover:bg-primary/20 hover:text-primary'
              } flex items-center rounded-full px-4 py-1 text-lg font-bold transition-all duration-300 ease-in-out`}
              href="/create"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={'mr-2'}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.00167 0.571289C6.43797 0.571289 5.17393 0.618438 3.86614 0.708353C2.47994 0.803659 1.37708 1.90788 1.2899 3.29682C1.1946 4.81538 1.14453 6.38843 1.14453 7.99986C1.14453 9.61129 1.1946 11.1843 1.28991 12.7029C1.37708 14.0919 2.47994 15.1961 3.86614 15.2914C5.17393 15.3812 6.43797 15.4284 8.00167 15.4284C9.56538 15.4284 10.8294 15.3812 12.1372 15.2914C13.5234 15.1961 14.6262 14.0919 14.7134 12.7029C14.8088 11.1843 14.8588 9.61129 14.8588 7.99986C14.8588 7.34721 14.8506 6.70087 14.8345 6.06191C14.8204 5.50267 14.6389 4.95616 14.3045 4.50047C13.2717 3.09346 12.4344 2.2022 11.0675 1.14722C10.6047 0.790062 10.0407 0.599134 9.46523 0.586558C9.00151 0.576424 8.51777 0.571289 8.00167 0.571289ZM8.00167 4.56515C8.39617 4.56515 8.71596 4.88495 8.71596 5.27944V7.28557H10.7221C11.1166 7.28557 11.4364 7.60537 11.4364 7.99986C11.4364 8.39435 11.1166 8.71415 10.7221 8.71415H8.71596V10.7203C8.71596 11.1148 8.39617 11.4346 8.00167 11.4346C7.60718 11.4346 7.28739 11.1148 7.28739 10.7203V8.71415H5.28125C4.88676 8.71415 4.56697 8.39435 4.56697 7.99986C4.56697 7.60537 4.88676 7.28557 5.28125 7.28557H7.28739V5.27944C7.28739 4.88495 7.60718 4.56515 8.00167 4.56515Z"
                  fill="currentColor"
                />
              </svg>
              <span className={'hidden lg:flex'}>Create</span>
            </Link>

            {isLoggedIn && (
              <Link
                className={`${
                  pathname?.includes('/projects')
                    ? 'bg-primary/20 text-primary'
                    : 'text-white hover:bg-primary/20 hover:text-primary'
                } flex items-center rounded-full px-4 py-1 text-lg font-bold transition-all duration-300 ease-in-out`}
                href="/projects"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={'mr-2'}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M1.33333 13.6667H10.3333C11.0667 13.6667 11.6667 13.0667 11.6667 12.3333V10.0667L16 11.5333V4.19999L11.6667 5.66667V3.33333C11.6667 2.6 11.0667 2 10.3333 2H1.33333C0.6 2 0 2.6 0 3.33333V12.3333C0 13.0667 0.6 13.6667 1.33333 13.6667Z"
                    fill="currentColor"
                  />
                </svg>
                <span className={'hidden lg:flex'}>Projects</span>
              </Link>
            )}

            {isLoggedIn && (
              <>
                {isProUser ? (
                  <Link
                    className={`${
                      pathname?.includes('/account-media')
                        ? 'bg-primary/20 text-primary'
                        : 'text-white hover:bg-primary/20 hover:text-primary'
                    } flex items-center rounded-full px-4 py-1 text-lg font-bold transition-all duration-300 ease-in-out`}
                    href="/account-media"
                  >
                    <ImageIconSvg className="mr-2 h-4 w-4" />
                    <span className={'hidden lg:flex'}>Media</span>
                  </Link>
                ) : (
                  <Paywall>
                    <div
                      className={`${
                        pathname?.includes('/account-media')
                          ? 'bg-primary/20 text-primary'
                          : 'text-white hover:bg-primary/20 hover:text-primary'
                      } flex cursor-pointer items-center rounded-full px-4 py-1 text-lg font-bold transition-all duration-300 ease-in-out`}
                    >
                      <ImageIconSvg className="mr-2 h-4 w-4" />
                      <span className={'hidden lg:flex'}>Media</span>
                    </div>
                  </Paywall>
                )}
              </>
            )}

            <div className="relative group">
              <div
                className="absolute -inset-1 bg-gradient-to-r from-primary to-primary/60 rounded-full blur opacity-35 group-hover:opacity-30 transition duration-1000 group-hover:duration-200">
              </div>
              <a
                className="relative flex items-center rounded-full px-4 py-1 text-lg font-bold text-white bg-[#1a1a1a] transition-all duration-300 ease-in-out hover:bg-primary/10 hover:text-primary"
                target="_blank"
                rel="noreferrer"
                href="https://beatwaveapp.com/tutorials/"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="mr-2 h-5 w-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                  />
                </svg>
                <span className={'hidden lg:flex'}>Tutorials</span>
              </a>
            </div>

            <button
              onClick={() => (window as any).chatbase.open()}
              className="flex items-center rounded-full px-2 py-1 text-lg font-bold text-white transition-all duration-300 ease-in-out hover:bg-primary/20 hover:text-primary"
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="mr-2 h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
                />
              </svg>
              <span className={'hidden lg:flex'}>Help</span>
            </button>
          </>
        )}
      </div>
      <div className="flex flex-row items-center space-x-2 justify-self-end">
        {isLoggedIn !== null &&
          (isLoggedIn ? (
            <AccountSettingsDialog>
              <button>
                <span className="mx-2 flex h-9 w-9 items-center justify-center rounded-full border-2 border-slate-700 text-slate-700">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.6515 20.4054C20.2043 20.2902 20.5336 19.7117 20.2589 19.2183C19.6533 18.1307 18.6993 17.1749 17.4788 16.4465C15.907 15.5085 13.9812 15 12 15C10.0188 15 8.09292 15.5085 6.52112 16.4465C5.30069 17.1749 4.34666 18.1307 3.74108 19.2183C3.46638 19.7117 3.79562 20.2902 4.34843 20.4054V20.4054C9.39524 21.4572 14.6047 21.4572 19.6515 20.4054V20.4054Z"
                      fill="currentColor"
                    />
                    <circle cx="12" cy="8" r="5" fill="currentColor" />
                  </svg>
                </span>
              </button>
            </AccountSettingsDialog>
          ) : (
            <Link
              href={'/login'}
              className={cx(
                'inline-flex w-full select-none justify-center rounded-full px-6 py-2 text-lg font-bold uppercase italic leading-none',
                'text-highlights-white hover:bg-wave-dark hover:text-wave-brown',
                'border border-transparent',
                'focus:outline-none focus-visible:bg-wave-dark focus-visible:text-highlights-white focus-visible:ring focus-visible:ring-wave-brown focus-visible:ring-opacity-75'
              )}
            >
              Login
            </Link>
          ))}
      </div>
    </div>
  );
}